// A new bill: drop the vendor's PDF or photo (it shows beside the form), fill in the vendor, their invoice number,
// dates and amount, and say what it's for: a PO (its deposit, the balance, or what's been received; the lines and
// amount come from the PO), a run's cost or a tooling item, or nothing (a bill on its own). The file is attached to
// the bill once it's saved.
import { FileUp } from "lucide-react";
import { type DragEvent, useEffect, useMemo, useState } from "react";
import { BILL_KIND_LABELS, BILL_KINDS, type BillKind, billFromPo, moneyText, type PoBillPart, vendorLabelOf } from "../../../../shared/bills.ts";
import { numberOf } from "../../../../shared/calc.ts";
import { mulRound } from "../../../../shared/po.ts";
import { COST_CATEGORY_LABELS, type CostCategory } from "../../../../shared/runs.ts";
import { amount, dayOf, useBillIndex } from "../../lib/bills.ts";
import { mutateWithResult } from "../../lib/mutate.ts";
import { uploadAttachment } from "../../ui/Attachments.tsx";
import { Button, cx, Input, Select, Textarea, UpperInput } from "../../ui/controls.tsx";
import { DialogLayer } from "../../ui/DialogLayer.tsx";
import { toast } from "../../ui/toast.tsx";

const PART_LABELS: Record<PoBillPart, string> = {
  deposit: "Its deposit",
  balance: "Everything not billed yet (the balance)",
  received: "What's been received and not billed",
};

export function NewBillDialog({
  vendorId,
  onClose,
  poId: presetPo,
  part: presetPart,
}: {
  vendorId?: string | null;
  poId?: string;
  part?: PoBillPart;
  onClose: (id?: string) => void;
}) {
  const ix = useBillIndex();
  const [id] = useState(() => crypto.randomUUID());
  const presetVendor = presetPo ? ix.poById.get(presetPo)?.vendor_id : vendorId;
  const [vendor, setVendor] = useState(presetVendor ?? "");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [invoice, setInvoice] = useState("");
  const [billDate, setBillDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [forWhat, setForWhat] = useState(presetPo ? `po:${presetPo}` : "");
  const [part, setPart] = useState<PoBillPart>(presetPart ?? "balance");
  const [kind, setKind] = useState<BillKind>("full");
  const [amountText, setAmountText] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [description, setDescription] = useState("");
  const [approve, setApprove] = useState(false);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);

  useEffect(() => {
    if (!file) return setPreview(null);
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  useEffect(() => {
    const v = ix.vendorById.get(vendor);
    if (v?.currency) setCurrency(v.currency);
  }, [vendor, ix]);

  const vendors = useMemo(() => ix.d.vendors.filter((v) => !v.archived).sort((a, b) => vendorLabelOf(a).localeCompare(vendorLabelOf(b))), [ix]);
  const [targetKind, targetId] = forWhat.split(":");
  const pos = ix.d.pos.filter((p) => p.vendor_id === vendor && p.status !== "draft" && p.status !== "cancelled");
  const costs = ix.d.costs.filter((c) => !ix.linesByCost.has(c.id) && (!c.vendor_id || c.vendor_id === vendor));
  const tooling = ix.d.tooling.filter((t) => !ix.linesByTooling.has(t.id) && (!t.vendor_id || t.vendor_id === vendor));
  const fromPo = targetKind === "po" ? billFromPo(ix, targetId, part, dayOf) : null;
  const cost = targetKind === "cost" ? ix.costById.get(targetId) : undefined;
  const tool = targetKind === "tool" ? ix.toolingById.get(targetId) : undefined;
  const linkedAmount = fromPo && !fromPo.problem ? fromPo.amount : cost ? usdOf(cost) : tool ? usdOf(tool) : null;
  const typed = numberOf(amountText);
  const finalAmount = amountText.trim() ? typed : (linkedAmount ?? NaN);

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  const save = async () => {
    if (!vendor) return toast.error("Pick the vendor");
    if (!(finalAmount >= 0) || Number.isNaN(finalAmount)) return toast.error("Enter the bill's amount");
    if (fromPo?.problem) return toast.error(fromPo.problem);
    setBusy(true);
    let ok: boolean;
    if (fromPo) {
      ({ ok } = await mutateWithResult("bill.create_from_po", {
        id,
        po_id: targetId,
        part,
        invoice_number: invoice,
        bill_date: billDate || null,
        due_date: dueDate || null,
        approve,
      }));
      // What the vendor's invoice says, if it differs from the PO: the 3-way match shows the difference.
      if (ok && amountText.trim() && Math.round(typed * 100) !== Math.round(fromPo.amount * 100))
        await mutateWithResult("bill.update", { id, patch: { amount: typed, ...(description ? { description } : {}) } });
      else if (ok && description) await mutateWithResult("bill.update", { id, patch: { description } });
    } else {
      const lines =
        cost || tool
          ? [
              {
                kind: "charge" as const,
                description: cost ? cost.description || COST_CATEGORY_LABELS[cost.category as CostCategory] : (tool?.name ?? ""),
                production_cost_id: cost?.id ?? null,
                tooling_item_id: tool?.id ?? null,
                amount: linkedAmount ?? finalAmount,
              },
            ]
          : [];
      ({ ok } = await mutateWithResult("bill.create", {
        id,
        vendor_id: vendor,
        kind: cost ? kindOfCategory(cost.category) : tool ? "tooling" : kind,
        invoice_number: invoice,
        bill_date: billDate || null,
        due_date: dueDate || null,
        // A run cost or tooling item is linked by its USD amount.
        currency: cost || tool ? "USD" : currency,
        amount: finalAmount,
        description,
        lines,
        approve,
      }));
    }
    if (ok && file)
      await uploadAttachment({ type: "bill", id }, file, { kind: "invoice", docDate: billDate || null }).catch((e) => toast.error((e as Error).message));
    setBusy(false);
    if (ok) {
      toast.ok("Bill saved");
      onClose(id);
    }
  };

  const forOptions = [
    { value: "", label: "Nothing in particular (a bill on its own)" },
    ...pos.map((p) => ({ value: `po:${p.id}`, label: `${p.number}${p.vendor_ref ? ` (${p.vendor_ref})` : ""}` })),
    ...costs.map((c) => ({
      value: `cost:${c.id}`,
      label: `Run cost ${ix.runById.get(c.run_id)?.number ?? ""}: ${c.description || COST_CATEGORY_LABELS[c.category as CostCategory]} (${moneyText(Number(c.amount), c.currency)})`,
    })),
    ...tooling.map((t) => ({ value: `tool:${t.id}`, label: `Tooling: ${t.name} (${moneyText(Number(t.amount), t.currency)})` })),
  ];

  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()} role="presentation">
      <div className="grid w-[min(1040px,calc(100vw-32px))] grid-cols-1 overflow-hidden rounded-xl border border-line bg-surface shadow-pop md:grid-cols-[1fr_440px]">
        <div
          className={cx(
            "flex min-h-[420px] flex-col border-b border-line bg-surface-2/50 md:border-r md:border-b-0",
            over && "outline-2 -outline-offset-4 outline-accent outline-dashed",
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={onDrop}
        >
          {preview && file ? (
            <>
              <div className="flex items-center gap-2 border-b border-line px-3 py-1.5 text-[12px]">
                <span className="truncate font-medium">{file.name}</span>
                <button type="button" className="ml-auto text-muted hover:underline" onClick={() => setFile(null)}>
                  Remove
                </button>
              </div>
              {file.type.startsWith("image/") ? (
                <img src={preview} alt={file.name} className="min-h-0 flex-1 object-contain p-2" />
              ) : (
                <iframe title={file.name} src={preview} className="min-h-[520px] w-full flex-1 bg-white" />
              )}
            </>
          ) : (
            <label className="flex flex-1 cursor-pointer flex-col items-center justify-center gap-2 p-8 text-center text-muted">
              <FileUp className="size-8 text-faint" />
              <span className="font-medium text-text">Drop the vendor's invoice here</span>
              <span className="text-[12px]">A PDF or a photo; it's kept with the bill and goes in the bookkeeper pack.</span>
              <span className="text-[12px] text-accent-text underline">or choose a file</span>
              <input type="file" accept="application/pdf,image/*" hidden onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </label>
          )}
        </div>
        <div className="p-4">
          <div className="text-[15px] font-semibold">New bill</div>
          <div className="mt-0.5 text-[12px] text-muted">What the vendor sent you to pay. It starts as a draft; approve it when it's checked.</div>
          <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-[12px]">
            <label className="col-span-2 flex flex-col gap-1">
              <span className="text-muted">Vendor</span>
              <Select
                value={vendor}
                options={[{ value: "", label: "Choose a vendor…" }, ...vendors.map((v) => ({ value: v.id, label: vendorLabelOf(v) }))]}
                onChange={(e) => {
                  setVendor(e.target.value);
                  setForWhat("");
                }}
              />
            </label>
            <label className="col-span-2 flex flex-col gap-1">
              <span className="text-muted">What it's for</span>
              <Select value={forWhat} options={forOptions} onChange={(e) => setForWhat(e.target.value)} disabled={!vendor} />
            </label>
            {targetKind === "po" && (
              <label className="col-span-2 flex flex-col gap-1">
                <span className="text-muted">Which part of the PO</span>
                <Select
                  value={part}
                  options={Object.entries(PART_LABELS).map(([value, label]) => ({ value, label }))}
                  onChange={(e) => setPart(e.target.value as PoBillPart)}
                />
                {fromPo?.problem ? (
                  <span className="text-warn">{fromPo.problem}</span>
                ) : (
                  fromPo && (
                    <span className="text-muted">
                      {fromPo.lines.length} {fromPo.lines.length === 1 ? "line" : "lines"} from the PO ·{" "}
                      <span className="font-mono text-text">{amount(fromPo.amount, ix.poById.get(targetId)?.currency ?? "USD")}</span>
                      {fromPo.lines.some((l) => l.kind === "deposit_credit") && " (deposit taken off)"}
                    </span>
                  )
                )}
              </label>
            )}
            <label className="flex flex-col gap-1">
              <span className="text-muted">Their invoice number</span>
              <Input value={invoice} onChange={(e) => setInvoice(e.target.value)} placeholder="INV-11998, TH2026…" className="font-mono" />
            </label>
            {!targetKind ? (
              <label className="flex flex-col gap-1">
                <span className="text-muted">Kind</span>
                <Select
                  value={kind}
                  options={BILL_KINDS.map((k) => ({ value: k, label: BILL_KIND_LABELS[k] }))}
                  onChange={(e) => setKind(e.target.value as BillKind)}
                />
              </label>
            ) : (
              <span />
            )}
            <label className="flex flex-col gap-1">
              <span className="text-muted">Dated (on the invoice)</span>
              <Input type="date" value={billDate} onChange={(e) => setBillDate(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-muted">Due</span>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              {!dueDate && targetKind === "po" && (
                <span className="text-faint">{part === "deposit" ? "Blank: due when the PO is acknowledged" : "Blank: due before it ships"}</span>
              )}
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-muted">Amount on the invoice</span>
              <div className="flex gap-1.5">
                <Input
                  value={amountText}
                  inputMode="decimal"
                  placeholder={linkedAmount != null ? mulRound(linkedAmount, 1, 2).toFixed(2) : "0.00"}
                  onChange={(e) => setAmountText(e.target.value)}
                  className="font-mono"
                />
                <UpperInput
                  value={currency}
                  maxLength={3}
                  onText={setCurrency}
                  className="w-16 font-mono"
                  aria-label="Currency"
                  disabled={targetKind === "po"}
                />
              </div>
              {linkedAmount != null && amountText.trim() && Math.round(typed * 100) !== Math.round(linkedAmount * 100) && (
                <span className="text-warn">Differs from what it's for ({amount(linkedAmount, currency)}): the 3-way match will show it</span>
              )}
            </label>
            <span />
            <label className="col-span-2 flex flex-col gap-1">
              <span className="text-muted">Description (optional)</span>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-12"
                placeholder="e.g. Self-adhesive silicone pads, 2,000 pcs"
              />
            </label>
            <label className="col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={approve} onChange={(e) => setApprove(e.target.checked)} className="accent-[var(--accent)]" />
              <span>It's checked: approve it now (it goes on the Payments due board)</span>
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button onClick={() => onClose()}>Cancel</Button>
            <Button variant="primary" disabled={busy || !vendor} onClick={save}>
              {busy ? "Saving…" : "Save bill"}
            </Button>
          </div>
        </div>
      </div>
    </DialogLayer>
  );
}

const usdOf = (x: { amount: number; currency: string; usd_rate: number }) =>
  x.currency === "USD" ? Number(x.amount) : mulRound(Number(x.amount), Number(x.usd_rate), 2);
const kindOfCategory = (c: string): BillKind =>
  c === "freight" || c === "broker"
    ? "freight"
    : c === "tariff_duty"
      ? "duty"
      : c === "mold_tooling" || c === "fixture" || c === "certification" || c === "samples"
        ? "tooling"
        : "other";
